import { TextArea as BlueprintTextArea } from '@blueprintjs/core';
import { AddLarge } from '@carbon/icons-react';
import classNames from 'classnames';
import uniq from 'lodash/uniq';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import {
  Body,
  Button,
  ChipInput,
  Input,
  InputButton,
  LoadingIcon,
  Modal,
  Textarea,
  Tooltip,
} from 'vet-bones/design-system/components';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  ADD_ORGANIZATION_DIALOG_ADMINS_ADD,
  ADD_ORGANIZATION_DIALOG_ADMINS_CHIP_ITEM,
  ADD_ORGANIZATION_DIALOG_ADMINS_CHIP_REMOVE,
  ADD_ORGANIZATION_DIALOG_ADMINS_INPUT,
  ADD_ORGANIZATION_DIALOG_CANCEL,
  ADD_ORGANIZATION_DIALOG_DESCRIPTION_INPUT,
  ADD_ORGANIZATION_DIALOG_DOMAIN_INPUT,
  ADD_ORGANIZATION_DIALOG_NAME_INPUT,
  ADD_ORGANIZATION_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { queueNotification } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useAddOrganizationMutation,
  useListOrganizationsQuery,
} from 'src/ui/graph/types';
import { useProfile } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';
import { buildDashboardRoute } from 'src/ui/utils/buildRoute';
import { isValidEmail } from 'src/ui/utils/isValidEmail';

export const AddOrganizationDialog: React.VFC = () => {
  const isMounted = useIsMounted();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const onError = useOnError();
  const onMutate = useOnMutate();
  const history = useHistory();

  const { data: profile } = useProfile();

  const isOpen = useAppSelector(
    isDialogOpenSelector(DialogType.ADD_ORGANIZATION)
  );

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [domain, setDomain] = React.useState('');
  const [admins, setAdmins] = React.useState<string[]>([]);
  const [adminsQuery, setAdminsQuery] = React.useState('');
  const [adminsError, setAdminsError] = React.useState<string | null>(null);

  const textAreaRef = React.useRef<BlueprintTextArea>(null);
  React.useEffect(() => {
    if (!description && textAreaRef.current?.textareaElement) {
      // reset text area height when description is cleared
      textAreaRef.current.setState({ height: undefined });
    }
  }, [description, textAreaRef]);

  const {
    isLoading,
    mutate: addOrganization,
  } = useAddOrganizationMutation(graphQLClient, { onMutate, onError });

  const handleClose = () => {
    setName('');
    setDescription('');
    setDomain('');
    setAdmins([]);
    setAdminsQuery('');
    setAdminsError(null);
    dispatch(closedDialog());
  };

  const handleAddAdmins = (newAdmins: string[]) => {
    const valid: string[] = [];
    const invalid: string[] = [];
    newAdmins
      .map((admin) => admin.split(/\s*[\s,]\s*/))
      .flat()
      .forEach((admin) => {
        if (!admin) {
          return;
        }

        if (isValidEmail(admin)) {
          valid.push(admin);
        } else {
          invalid.push(admin);
        }
      });

    setAdminsError(
      invalid.length
        ? copy.components.organization.dialog.inputs.email.validation(invalid)
        : null
    );

    if (valid.length) {
      setAdmins((prev) => uniq([...prev, ...valid]));
    }

    setAdminsQuery(invalid.join(' '));
    return false;
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    const trimmedDomain = domain.trim();

    if (!trimmedName || !trimmedDomain || isLoading) {
      return;
    }

    addOrganization(
      {
        input: {
          name: trimmedName,
          domain: trimmedDomain.toLowerCase(),
          description: description.trim() || undefined,
          admins: admins.length ? admins : undefined,
        },
      },
      {
        onSuccess: async (data) => {
          const org = data.addOrganization?.organization;
          if (!org?.id) {
            return;
          }

          data.addOrganization?.invitationResults?.forEach((result) => {
            if (result && !result.success) {
              dispatch(
                queueNotification(
                  `Error inviting ${result.email}: ${result.error}`
                )
              );
            }
          });

          await queryClient.invalidateQueries(
            useListOrganizationsQuery.getKey()
          );
          if (!isMounted.current) {
            return;
          }

          if (org.domain) {
            history.push(buildDashboardRoute(org.domain));
          }
          handleClose();
        },
      }
    );
  };

  let domainValidationError: string | undefined;
  if (!domain.trim()) {
    // ignore
  } else if (!domain.trim().match(/^[a-z]/)) {
    domainValidationError =
      copy.components.organization.dialog.inputs.domain.validationStart;
  } else if (!domain.trim().match(/^[a-z][a-z0-9-]*$/)) {
    domainValidationError =
      copy.components.organization.dialog.inputs.domain.validationChars;
  } else if (!domain.trim().match(/^[a-z][a-z0-9]*(-[a-z0-9]+)*$/)) {
    domainValidationError =
      copy.components.organization.dialog.inputs.domain.validationHyphens;
  }
  const isDomainInvalid = Boolean(domainValidationError);

  let validationError: string;
  if (!name.trim()) {
    validationError =
      copy.components.organization.dialog.inputs.name.validationRequired;
  } else if (!domain.trim()) {
    validationError =
      copy.components.organization.dialog.inputs.domain.validationRequired;
  } else if (domainValidationError) {
    validationError = domainValidationError;
  } else {
    validationError = '';
  }

  const isSubmitDisabled = Boolean(validationError || isLoading);

  let domainHelp: string | undefined;
  if (isDomainInvalid) {
    domainHelp = domainValidationError;
  } else if (domain) {
    domainHelp = copy.components.organization.dialog.inputs.domain.help(domain);
  }

  return (
    <Modal
      canOutsideClickClose={false}
      className="organization-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={ADD_ORGANIZATION_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.organization.addDialog.buttons.cancel}
          </Button>
          <Tooltip
            description={validationError}
            disabled={isLoading || !isSubmitDisabled}
          >
            <Button
              disabled={isSubmitDisabled}
              leftIcon={isLoading ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={ADD_ORGANIZATION_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {copy.components.organization.addDialog.buttons.submit}
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.organization.addDialog.title}
    >
      <div className="organization-dialog-body">
        <div className="organization-dialog-container">
          <Input
            autoFocus
            clearable={false}
            disabled={isLoading}
            label={copy.components.organization.dialog.inputs.name.label}
            onChange={(e) => setName(e.target.value)}
            placeholder=""
            required
            size="md"
            testId={ADD_ORGANIZATION_DIALOG_NAME_INPUT}
            value={name}
          />
          <Body className="organization-dialog-input-footer" size="sm">
            {copy.components.organization.dialog.inputs.name.footer}
          </Body>
        </div>

        <Textarea
          className="organization-dialog-textarea"
          disabled={isLoading}
          growVertically
          label={copy.components.organization.dialog.inputs.description.label}
          onChange={(e) => setDescription(e.target.value)}
          placeholder=""
          rows={2}
          testId={ADD_ORGANIZATION_DIALOG_DESCRIPTION_INPUT}
          textAreaRef={textAreaRef}
          value={description}
        />

        <ChipInput
          clearable={false}
          disabled
          label={copy.components.organization.dialog.inputs.owner.label}
          size="md"
          values={profile?.profile?.email ? [profile.profile.email] : []}
        />

        <ChipInput
          chipButtonTestId={ADD_ORGANIZATION_DIALOG_ADMINS_CHIP_REMOVE}
          chipTestId={ADD_ORGANIZATION_DIALOG_ADMINS_CHIP_ITEM}
          className="organization-dialog-chip-input"
          clearable={false}
          disabled={isLoading}
          helpProps={adminsError ? { text: adminsError } : undefined}
          inputValue={adminsQuery}
          invalid={Boolean(adminsError)}
          label={copy.components.organization.dialog.inputs.admin.label}
          onAdd={handleAddAdmins}
          onInputChange={(e) => setAdminsQuery(e.currentTarget.value)}
          onRemove={(_, idx) => {
            setAdmins((prev) => {
              const next = [...prev];
              next.splice(idx, 1);
              return next;
            });
            setAdminsError(null);
          }}
          rightElement={
            adminsQuery.trim() ? (
              <InputButton
                icon={AddLarge}
                onClick={() => handleAddAdmins([adminsQuery])}
                testId={ADD_ORGANIZATION_DIALOG_ADMINS_ADD}
              />
            ) : undefined
          }
          size="md"
          testId={ADD_ORGANIZATION_DIALOG_ADMINS_INPUT}
          values={admins}
        />

        <Input
          className={classNames(
            'organization-dialog-input-with-help',
            'organization-dialog-input-monospace'
          )}
          clearable={false}
          disabled={isLoading}
          helpProps={domainHelp ? { text: domainHelp } : undefined}
          invalid={isDomainInvalid}
          label={copy.components.organization.dialog.inputs.domain.label}
          onChange={(e) => setDomain(e.target.value)}
          placeholder=""
          required
          size="md"
          testId={ADD_ORGANIZATION_DIALOG_DOMAIN_INPUT}
          value={domain}
        />
      </div>
    </Modal>
  );
};
